import { useCallback, useMemo, useState } from 'react';
import {
  DSButton,
  DSDrawer,
  DSIcon,
  DSModal,
  DSTextInput,
  DSTypography
} from '@eiv2/design-system';
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowParams } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { RequestState } from '../components/RequestState';
import { DateRangeFields, DateRangeValidationMessage } from '../components/DateRangePicker';
import { CollapsibleFiltersCard } from '../components/CollapsibleFiltersCard';
import { IconTooltipButton } from '../components/IconTooltipButton';
import { DataGridFrame, visibleDataGridRows } from '../components/DataGridFrame';
import { remarketingApi } from '../api/client';
import { useCompactViewport } from '../hooks/useCompactViewport';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { getDefaultWindow, type DateRange } from '../services/dateWindow';
import { formatDateTime, formatNumber } from '../services/format';
import { toastError, toastSuccess } from '../components/toast';
import { dataGridLocaleText } from '../theme/dataGridLocale';
import type { RemarketingLead } from '../types/api';
import { RemarketingJourneys } from '../components/remarketing/RemarketingJourneys';

export function RemarketingPage() {
  const [tab, setTab] = useState<'journeys' | 'leads'>('journeys');

  return (
    <div>
      <PageHeader
        title="Remarketing"
        subtitle="Jornadas e leads capturados para campanhas de recuperação."
      />

      <div
        aria-label="Remarketing"
        className="mb-6 flex gap-1 border-b border-gray-3"
        role="tablist"
      >
        {(
          [
            { id: 'journeys', label: 'Jornadas' },
            { id: 'leads', label: 'Leads' }
          ] as const
        ).map((item) => {
          const selected = tab === item.id;
          return (
            <button
              key={item.id}
              aria-controls={`panel-${item.id}`}
              aria-selected={selected}
              className={[
                'cursor-pointer border-b-2 px-4 py-2.5 text-[14px] font-semibold transition-colors duration-150',
                selected
                  ? 'border-brand-primary-3 text-brand-primary-3'
                  : 'border-transparent text-gray-5 hover:text-dark-3'
              ].join(' ')}
              id={`tab-${item.id}`}
              onClick={() => setTab(item.id)}
              role="tab"
              type="button"
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div aria-labelledby={`tab-${tab}`} id={`panel-${tab}`} role="tabpanel">
        {tab === 'journeys' ? <RemarketingJourneys /> : <RemarketingLeads />}
      </div>
    </div>
  );
}

/**
 * Leads de remarketing.
 *
 * PII (nome, e-mail, telefone) vive só no estado em memória desta tela — nada
 * de localStorage/sessionStorage, e os filtros que vão para a URL do BFF são
 * apenas IDs e datas. O e-mail só sai daqui no CORPO da exclusão.
 */
function RemarketingLeads() {
  const [dateWindow, setWindow] = useState<DateRange>(() => getDefaultWindow());
  const [eventId, setEventId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [source, setSource] = useState('');

  const [applied, setApplied] = useState({ eventId: '', organizationId: '', source: '' });

  const [leadToErase, setLeadToErase] = useState<RemarketingLead | null>(null);
  const [erasureConfirmation, setErasureConfirmation] = useState('');
  const [erasureBusy, setErasureBusy] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });
  const isMobile = useCompactViewport();

  const filters = useMemo(
    () => ({
      eventId: applied.eventId || undefined,
      organizationId: applied.organizationId || undefined,
      source: applied.source || undefined,
      createdFrom: dateWindow.from,
      createdTo: dateWindow.to
    }),
    [applied, dateWindow.from, dateWindow.to]
  );

  const fetcher = useCallback(
    async (cursor: string | null, signal: AbortSignal) => {
      const page = await remarketingApi.listLeads({ ...filters, limit: 50, cursor }, signal);
      return { data: page.leads, nextCursor: page.nextCursor };
    },
    [filters]
  );

  const list = usePaginatedList<RemarketingLead>(fetcher, [
    filters.eventId,
    filters.organizationId,
    filters.source,
    filters.createdFrom,
    filters.createdTo
  ]);

  const rows = useMemo(() => list.items.map((lead) => ({ ...lead, id: lead.id })), [list.items]);

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Nome', flex: 1, minWidth: 160 },
    { field: 'email', headerName: 'E-mail', flex: 1, minWidth: 200 },
    { field: 'phone', headerName: 'Telefone', width: 150 },
    { field: 'eventId', headerName: 'Evento', width: 120 },
    { field: 'organizationId', headerName: 'Organização', width: 130 },
    { field: 'source', headerName: 'Origem', width: 130 },
    {
      field: 'createdAt',
      headerName: 'Capturado em',
      width: 170,
      valueFormatter: (value: string) => formatDateTime(value)
    },
    {
      field: 'acoes',
      headerName: 'LGPD',
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <DSButton
          className="w-auto shrink-0 text-feedback-danger"
          onClick={() => {
            setLeadToErase(params.row as RemarketingLead);
            setErasureConfirmation('');
          }}
          type="button"
          variant="tertiary"
        >
          <DSIcon name="Trash" size={16} />
          Excluir
        </DSButton>
      )
    }
  ];

  const confirmationMatches =
    !!leadToErase && erasureConfirmation.trim().toLowerCase() === leadToErase.email.toLowerCase();

  async function confirmErasure() {
    if (!leadToErase || !confirmationMatches || erasureBusy) return;
    setErasureBusy(true);
    try {
      const result = await remarketingApi.eraseLead(leadToErase.email);
      setLeadToErase(null);
      list.reload();
      toastSuccess(
        result.deleted === 1
          ? 'Dados pessoais excluídos.'
          : `Dados pessoais excluídos (${result.deleted} registros).`
      );
    } catch (err) {
      toastError(
        err instanceof Error
          ? err.message
          : 'Não foi possível excluir os dados. Tente novamente.'
      );
    } finally {
      setErasureBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <CollapsibleFiltersCard storageKey="remarketing-leads" title="Filtrar leads">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied({ eventId, organizationId, source });
          }}
        >
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <DateRangeFields value={dateWindow} onChange={setWindow} />
              <DSTextInput
                classNames={{ field: 'min-h-14 bg-light-gray' }}
                label="Evento"
                onChange={(e) => setEventId(e.target.value)}
                value={eventId}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <DSTextInput
                classNames={{ field: 'min-h-14 bg-light-gray' }}
                label="Organização"
                onChange={(e) => setOrganizationId(e.target.value)}
                value={organizationId}
              />
              <DSTextInput
                classNames={{ field: 'min-h-14 bg-light-gray' }}
                label="Origem"
                onChange={(e) => setSource(e.target.value)}
                value={source}
              />
            </div>
            <DateRangeValidationMessage value={dateWindow} onChange={setWindow} />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <DSButton className="w-full sm:w-auto" type="submit">
              <DSIcon name="Filter" size={16} />
              Filtrar
            </DSButton>
            <DSButton
              className="w-full sm:w-auto"
              onClick={() => {
                setEventId('');
                setOrganizationId('');
                setSource('');
                setApplied({ eventId: '', organizationId: '', source: '' });
              }}
              type="button"
              variant="secondary"
            >
              Limpar filtros
            </DSButton>
            <a
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-[12px] bg-dark-light-mode-background px-4 text-[14px] font-semibold text-dark-3 transition-colors hover:bg-gray-4 sm:w-auto"
              href={remarketingApi.exportUrl(filters)}
            >
              <DSIcon name="DocumentDownload" size={16} />
              Exportar CSV
            </a>
          </div>
        </form>
      </CollapsibleFiltersCard>

      {list.error ? (
        <RequestState
          title="Não foi possível carregar os leads"
          error={list.error}
          onRetry={list.reload}
        />
      ) : list.isLoading && rows.length === 0 ? (
        <RequestState title="Carregando leads" message="Buscando o período selecionado." />
      ) : (
        <SectionCard
          action={
            <IconTooltipButton
              disabled={list.isLoading}
              label="Atualizar"
              onClick={list.reload}
            >
              <DSIcon name="Refresh" size={20} />
            </IconTooltipButton>
          }
          subtitle={`${formatNumber(rows.length)} ${rows.length === 1 ? 'lead carregado' : 'leads carregados'}`}
          title="Leads"
        >
          {rows.length === 0 ? (
            <DSTypography as="p" className="py-8 text-[13px] font-normal text-gray-5">
              Nenhum lead capturado com esses filtros.
            </DSTypography>
          ) : (
            <DataGridFrame
              visibleRowCount={visibleDataGridRows(
                rows.length,
                paginationModel.page,
                paginationModel.pageSize
              )}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                disableColumnMenu
                disableRowSelectionOnClick
                localeText={{
                  ...dataGridLocaleText,
                  noRowsLabel: 'Nenhum lead encontrado com esses filtros.'
                }}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                isRowSelectable={(_: GridRowParams) => false}
              />
            </DataGridFrame>
          )}
          <LoadMoreButton
            nextCursor={list.nextCursor}
            onLoadMore={list.loadMore}
            isLoading={list.isLoading || list.isLoadingMore}
          />
        </SectionCard>
      )}

      {(() => {
        const open = leadToErase !== null;
        const closeErasure = () => {
          if (!erasureBusy) setLeadToErase(null);
        };

        const actionsClass = isMobile
          ? 'flex flex-col gap-2'
          : 'flex flex-wrap items-center justify-end gap-2';
        const actionButtonClass = isMobile ? 'w-full' : 'w-auto';

        const content = (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <DSTypography
                as="h2"
                className="text-[16px] font-semibold text-dark-3"
                id="lgpd-erase-title"
              >
                Excluir dados pessoais?
              </DSTypography>

              <DSTypography as="p" className="text-[14px] font-normal text-gray-5">
                Ao excluir, o lead será removido de todos os eventos e as capturas ainda na fila serão
                descartadas. Esta ação não pode ser desfeita. Novas capturas futuras continuam
                sujeitas às regras de consentimento.
              </DSTypography>
              <DSTypography as="p" className="break-words text-[14px] font-normal text-gray-5">
                Para confirmar, digite o e-mail do lead:{' '}
                <strong className="font-semibold text-dark-3">{leadToErase?.email}</strong>.
              </DSTypography>

              <DSTextInput
                aria-label="Confirmação"
                autoComplete="off"
                classNames={{ field: 'min-h-14 bg-light-gray' }}
                label="Confirmação"
                onChange={(e) => setErasureConfirmation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                placeholder="E-mail do lead"
                value={erasureConfirmation}
              />
            </div>

            <div className={actionsClass}>
              <DSButton
                className={`${actionButtonClass} bg-feedback-danger hover:opacity-90`}
                disabled={!confirmationMatches || erasureBusy}
                onClick={() => void confirmErasure()}
                type="button"
              >
                {erasureBusy ? 'Excluindo…' : 'Excluir dados'}
              </DSButton>
              <DSButton
                className={actionButtonClass}
                disabled={erasureBusy}
                onClick={closeErasure}
                type="button"
                variant="secondary"
              >
                Cancelar
              </DSButton>
            </div>
          </div>
        );

        if (isMobile) {
          return (
            <DSDrawer
              aria-labelledby="lgpd-erase-title"
              className="flex max-h-[calc(100dvh-40px)] w-full flex-col overflow-hidden rounded-t-[24px] rounded-b-none"
              onClose={closeErasure}
              open={open}
              orientation="bottom"
              overlayClassName="bg-overlay/45"
              size="fit"
            >
              <div className="flex h-6 shrink-0 items-center justify-center" aria-hidden>
                <span className="h-1 w-10 rounded-full bg-gray-3" />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-6">{content}</div>
            </DSDrawer>
          );
        }

        return (
          <DSModal
            aria-labelledby="lgpd-erase-title"
            className="max-w-[480px] rounded-[24px] p-6"
            onClose={closeErasure}
            open={open}
            overlayClassName="bg-overlay/45"
          >
            {content}
          </DSModal>
        );
      })()}
    </div>
  );
}
